import { useDocumentsRepository } from '@Hooks/useRepository';
import { FilesRepository } from '@Repositories/Documents';
import { isNullOrEmpty, resolveToArrayBuffer } from '@synqo/frontend-component-library';
import { useRef, useCallback } from 'react';

/**
 * Provides functionality for loading files with lazy caching.
 * Files which are loaded from the server are simultaneously cached, so that subsequent requests can short-circuit.
 * It also provides methods to write directly into the cache, e.g. when a file has not been persisted to the server yet.
 **/
export function useLoadFile() {
    const blobCacheRef = useRef<Record<string, ArrayBuffer>>({});
    const files = useDocumentsRepository(FilesRepository);

    const loadFile = useCallback(
        async (fileId: string) => {
            const blobCache = blobCacheRef.current;

            if (isNullOrEmpty(fileId)) {
                return undefined;
            } else if (fileId in blobCache) {
                return blobCache[fileId];
            }

            const arrayBuffer = await files.downloadFile(fileId, resolveToArrayBuffer);
            blobCache[fileId] = arrayBuffer;

            return arrayBuffer;
        },
        [blobCacheRef, files],
    );

    const resetCache = useCallback(() => {
        blobCacheRef.current = {};
    }, [blobCacheRef]);

    const addToCache = useCallback(
        (fileId: string, arrayBuffer: ArrayBuffer) => {
            blobCacheRef.current[fileId] = arrayBuffer;
        },
        [blobCacheRef],
    );

    const removeFromCache = useCallback(
        (fileId: string) => {
            const blobCache = blobCacheRef.current;
            delete blobCache[fileId];
        },
        [blobCacheRef],
    );

    return {
        loadFile,
        addToCache,
        removeFromCache,
        resetCache,
    };
}
